import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Github, ExternalLink } from "lucide-react";

import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { Container, SectionLabel, CTASection } from "@/components/primitives";
import { getProject, PROJECTS } from "@/data/projects";

function Block({ label, title, children }) {
  return (
    <Reveal className="grid gap-6 border-t border-white/10 py-14 md:grid-cols-12 md:gap-12 md:py-20">
      <div className="md:col-span-4">
        <SectionLabel>{label}</SectionLabel>
        {title && (
          <h2 className="mt-6 font-cabinet text-2xl font-medium tracking-tight text-primary-ink md:text-3xl">
            {title}
          </h2>
        )}
      </div>
      <div className="md:col-span-8">{children}</div>
    </Reveal>
  );
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = getProject(slug);

  if (!project) return <Navigate to="/projects" replace />;

  const idx = PROJECTS.findIndex((p) => p.slug === slug);
  const next = PROJECTS[(idx + 1) % PROJECTS.length];

  return (
    <>
      <SEO
        title={project.name}
        description={`${project.name}. ${project.tagline}. ${project.summary}`}
        path={`/projects/${project.slug}`}
        image={project.cover}
      />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40" data-testid="case-study-hero">
        <Container>
          <Link
            to="/projects"
            data-testid="back-to-projects"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-muted-ink transition-colors hover:text-primary-ink"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
            All Projects
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10"
          >
            <p className="font-mono text-xs uppercase tracking-[0.18em] text-accent">
              {project.role} · {project.year}
            </p>
            <h1 className="mt-5 max-w-4xl font-cabinet text-4xl font-medium leading-[1.03] tracking-tight text-primary-ink sm:text-5xl lg:text-6xl">
              {project.name}
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-secondary-ink md:text-xl">
              {project.tagline}
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              {project.liveDemo && (
                <a
                  href={project.liveDemo}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="case-study-live-demo"
                  className="group inline-flex items-center gap-2 bg-accent px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-accent-hover"
                >
                  <ExternalLink className="h-4 w-4" />
                  Live Demo
                </a>
              )}
              {project.github ? (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="case-study-github"
                  className="group inline-flex items-center gap-2 border border-white/15 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-primary-ink transition-colors hover:border-white/40 hover:bg-white/5"
                >
                  <Github className="h-4 w-4" />
                  GitHub
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 border border-white/10 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-muted-ink">
                  <Github className="h-4 w-4" />
                  Private Repository
                </span>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="mt-14 overflow-hidden border border-white/10"
          >
            <img
              src={project.cover}
              alt={project.name}
              className="aspect-[16/9] w-full object-cover"
            />
          </motion.div>
        </Container>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <Container>
          <Block label="Overview">
            <p className="text-lg leading-relaxed text-secondary-ink">
              {project.overview}
            </p>
          </Block>

          <Block label="Business Problem" title="The business context">
            <p className="text-lg leading-relaxed text-secondary-ink">
              {project.problem}
            </p>
          </Block>

          <Block label="Approach" title="How I approached it">
            <p className="text-lg leading-relaxed text-secondary-ink">
              {project.solution}
            </p>
          </Block>

          <Block label="Architecture">
            <ul className="space-y-4">
              {project.architecture.map((a, i) => (
                <li key={i} className="flex gap-4">
                  <span className="mt-1 font-mono text-xs text-accent">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-lg leading-relaxed text-secondary-ink">
                    {a}
                  </span>
                </li>
              ))}
            </ul>
          </Block>

          <Block label="Technology">
            <div className="flex flex-wrap gap-3">
              {project.tech.map((t) => (
                <span
                  key={t}
                  className="border border-white/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.12em] text-secondary-ink"
                >
                  {t}
                </span>
              ))}
            </div>
          </Block>

          <Block label="Engineering Decisions">
            <div className="space-y-8">
              {project.decisions.map((d) => (
                <div key={d.title}>
                  <h3 className="font-cabinet text-xl font-medium tracking-tight text-primary-ink">
                    {d.title}
                  </h3>
                  <p className="mt-2 text-lg leading-relaxed text-secondary-ink">
                    {d.body}
                  </p>
                </div>
              ))}
            </div>
          </Block>

          <Block label="Screenshots">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {project.gallery.map((g, i) => (
                <div
                  key={i}
                  className={`overflow-hidden border border-white/10 ${
                    i === 0 ? "sm:col-span-2" : ""
                  }`}
                >
                  <img
                    src={g}
                    alt={`${project.name} screenshot ${i + 1}`}
                    loading="lazy"
                    className="aspect-video w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
                  />
                </div>
              ))}
            </div>
          </Block>

          <Block label="Challenges">
            <p className="text-lg leading-relaxed text-secondary-ink">
              {project.challenges}
            </p>
          </Block>

          <Block label="Business Outcome" title="Business impact">
            <p className="text-lg leading-relaxed text-secondary-ink">
              {project.outcome}
            </p>
          </Block>

          <Block label="Lessons Learned">
            <p className="text-lg leading-relaxed text-secondary-ink">
              {project.lessons}
            </p>
          </Block>

          {/* Next project */}
          <Reveal className="border-t border-white/10 pt-14 md:pt-20">
            <Link
              to={`/projects/${next.slug}`}
              data-testid="next-project"
              className="group flex flex-wrap items-center justify-between gap-4"
            >
              <div>
                <p className="overline mb-3">Next Project</p>
                <h3 className="font-cabinet text-3xl font-medium tracking-tight text-primary-ink transition-colors group-hover:text-accent md:text-5xl">
                  {next.name}
                </h3>
              </div>
              <ArrowUpRight className="h-10 w-10 text-muted-ink transition-all group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-accent" />
            </Link>
          </Reveal>
        </Container>
      </section>

      <CTASection />
    </>
  );
}
